/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayPauseShortcut } from '../../../hooks/useAudioPlayPauseShortcut';

const i18n = defineMessages({
	playButtonLabel: {
		id: 'audio.mini-play-pause-button.play-button-label',
		defaultMessage: 'Play',
		description:
			'Accessible label for the mini play button in the audio player that starts audio playback when clicked.',
	},
	pauseButtonLabel: {
		id: 'audio.mini-play-pause-button.pause-button-label',
		defaultMessage: 'Pause',
		description:
			'Accessible label for the mini pause button in the audio player that pauses audio playback when clicked.',
	},
});

const styles = cssMap({
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral'),
	},
	buttonHover: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	},
	icon: {
		display: 'inline-block',
		transform: 'scale(0.875)',
	},
});

export type MiniPlayButtonProps = {
	isPlaying: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
};

export const MiniPlayPauseButton = ({
	isPlaying,
	onMouseEnter,
	onMouseLeave,
}: MiniPlayButtonProps) => {
	const { formatMessage } = useIntl();
	const { audioRef } = useAudioPlayerStreamingContext();
	const { trackAIResultAction } = useAIEventsInstrumentation();
	const updateTooltip = useRef<() => void>();

	const { buttonContent, testId, tooltipLabel } = useMemo(() => {
		let buttonContent: React.ReactNode;
		let testId: string;
		let tooltipLabel: string;

		if (isPlaying) {
			tooltipLabel = formatMessage(i18n.pauseButtonLabel);
			buttonContent = <VideoPauseOverlayIcon label={tooltipLabel} />;
			testId = 'miniplayer-mini-pause-button';
		} else {
			tooltipLabel = formatMessage(i18n.playButtonLabel);
			buttonContent = <VideoPlayOverlayIcon label={tooltipLabel} />;
			testId = 'miniplayer-mini-play-button';
		}

		return { buttonContent, testId, tooltipLabel };
	}, [isPlaying, formatMessage]);

	const handleClick = useCallback(() => {
		let action: string;

		if (isPlaying) {
			action = 'audioPause';
			audioRef.current?.pause();
		} else {
			action = 'audioPlay';
			audioRef.current?.play();
		}

		trackAIResultAction(action, {
			attributes: {
				source: 'minified-audio-player',
			},
		});
	}, [audioRef, isPlaying, trackAIResultAction]);

	useAudioPlayPauseShortcut(handleClick);

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [isPlaying]);

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return tooltipLabel;
			}}
		>
			<Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
				<Pressable
					onClick={handleClick}
					xcss={cx(styles.button, styles.buttonHover)}
					testId={testId}
				>
					<Box as="span" xcss={styles.icon}>
						{buttonContent}
					</Box>
				</Pressable>
			</Box>
		</Tooltip>
	);
};
